import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { getFund, getSpecificPortfolioV3 } from 'venn-api';
import { BenchmarkConstraintManagement, PortfolioLabContext } from 'venn-components';
import { proxySetIdState } from 'venn-state';
import { isNil } from 'lodash';
import { Loading, Notifications, NotificationType, portalMenuIgnoreActivityClassName } from 'venn-ui-kit';
import type { BenchmarkConstraint } from 'venn-utils';
import { AnalysisSubject, logExceptionIntoSentry, useApi } from 'venn-utils';
import ConstraintsPolicyStatusFooter from './ConstraintsPolicyStatusFooter';

const BENCHMARK_INVALID_MESSAGE = 'You do not have permission to access this item.';

const handleBenchmarkError = (e: unknown) => {
  const error = e as { name?: string } | undefined;
  if (error?.name === 'AbortError') {
    return;
  }
  Notifications.notify(
    BENCHMARK_INVALID_MESSAGE,
    NotificationType.ERROR,
    'bottom-right',
    portalMenuIgnoreActivityClassName,
  );
  logExceptionIntoSentry(e as Parameters<typeof logExceptionIntoSentry>[0]);
};

interface BenchmarkConstraintsProps {
  isSideBarMaximized: boolean;
  isInEditState: boolean;
  setIsInEditState: React.Dispatch<React.SetStateAction<boolean>>;
}

const BenchmarkConstraints = ({ isSideBarMaximized, isInEditState, setIsInEditState }: BenchmarkConstraintsProps) => {
  const {
    portfolio,
    loadingPolicy,
    policyError,
    policyBenchmarkConstraint,
    benchmarkConstraint,
    onUpdateBenchmarkConstraint,
    onApplyBenchmarkConstraintToPolicy,
    onResetToPolicyBenchmarkConstraint,
  } = useContext(PortfolioLabContext);

  const [localConstraint, setLocalConstraintState] = useState<BenchmarkConstraint | undefined>(benchmarkConstraint);
  const [localBenchmarkSubject, setLocalBenchmarkSubject] = useState<AnalysisSubject | undefined>();

  const proxySetId = useRecoilValue(proxySetIdState);
  const getPortfolioApiRef = useRef(useApi(getSpecificPortfolioV3));
  const getFundApiRef = useRef(useApi(getFund));

  const setLocalConstraint = useCallback(
    (constraint: BenchmarkConstraint | undefined) => {
      // The app boots via legacy ReactDOM.render, so updates made after an `await` (e.g. resolving
      // a portfolio-type benchmark) aren't auto-batched. Without this, isInEditState can commit in
      // its own render before localConstraint catches up, letting the hasNoChanges effect below
      // reset isInEditState before the real constraint value ever lands.
      unstable_batchedUpdates(() => {
        !isInEditState && setIsInEditState(true);
        setLocalConstraintState(constraint);
      });
    },
    [isInEditState, setIsInEditState],
  );

  const resolveBenchmarkSubject = useCallback(
    async (constraint: BenchmarkConstraint | undefined) => {
      if (isNil(constraint) || (isNil(constraint.benchmarkFundId) && isNil(constraint.benchmarkStrategyId))) {
        setLocalBenchmarkSubject(undefined);
        return;
      }
      try {
        if (!isNil(constraint.benchmarkStrategyId)) {
          const { content } = await getPortfolioApiRef.current(constraint.benchmarkStrategyId, undefined, proxySetId);
          setLocalBenchmarkSubject(new AnalysisSubject(content, 'portfolio', undefined));
        } else if (!isNil(constraint.benchmarkFundId)) {
          const { content } = await getFundApiRef.current(constraint.benchmarkFundId, proxySetId);
          setLocalBenchmarkSubject(new AnalysisSubject(content, 'investment', undefined));
        }
      } catch (e) {
        handleBenchmarkError(e);
        setLocalBenchmarkSubject(undefined);
      }
    },
    [proxySetId],
  );

  useEffect(() => {
    setLocalConstraintState(benchmarkConstraint);
    resolveBenchmarkSubject(benchmarkConstraint);
  }, [benchmarkConstraint, resolveBenchmarkSubject]);

  const onChangeBenchmarkSubject = useCallback(
    async (subject: AnalysisSubject | undefined) => {
      if (isNil(subject)) {
        setLocalBenchmarkSubject(undefined);
        if (!isNil(localConstraint)) {
          setLocalConstraint(undefined);
        }
        return;
      }

      const portfolioId = subject.portfolio?.id;
      if (subject.type === 'portfolio' && !isNil(portfolioId)) {
        try {
          const { content } = await getPortfolioApiRef.current(portfolioId, undefined, proxySetId);
          const resolved = new AnalysisSubject(content, 'portfolio', undefined);
          setLocalBenchmarkSubject(resolved);
          setLocalConstraint({
            benchmarkStrategyId: content.id,
            benchmarkFundId: undefined,
            benchmarkName: content.name,
            maxTrackingError: undefined,
            isInPortfolioPolicy: false,
          });
        } catch (e) {
          handleBenchmarkError(e);
          setLocalBenchmarkSubject(undefined);
          setLocalConstraint(undefined);
        }
        return;
      }

      const fundId = subject.fund?.id;
      if (isNil(fundId)) {
        return;
      }
      try {
        const { content } = await getFundApiRef.current(fundId, proxySetId);
        setLocalBenchmarkSubject(new AnalysisSubject(content, 'investment', undefined));
        setLocalConstraint({
          benchmarkFundId: content.id,
          benchmarkStrategyId: undefined,
          benchmarkName: content.name,
          maxTrackingError: undefined,
          isInPortfolioPolicy: false,
        });
      } catch (e) {
        handleBenchmarkError(e);
        setLocalBenchmarkSubject(undefined);
        setLocalConstraint(undefined);
      }
    },
    [localConstraint, setLocalConstraint, proxySetId],
  );

  const onChangeMaxTrackingError = useCallback(
    (value: number | undefined) => {
      if (isNil(localConstraint) || localConstraint.maxTrackingError === value) {
        return;
      }
      setLocalConstraint({ ...localConstraint, maxTrackingError: value });
    },
    [localConstraint, setLocalConstraint],
  );

  const onConstraintsSave = useCallback(() => {
    setIsInEditState(false);
    onUpdateBenchmarkConstraint(localConstraint);
  }, [setIsInEditState, onUpdateBenchmarkConstraint, localConstraint]);

  const onConstraintsRevert = useCallback(() => {
    setIsInEditState(false);
    setLocalConstraintState(benchmarkConstraint);
    resolveBenchmarkSubject(benchmarkConstraint);
  }, [setIsInEditState, benchmarkConstraint, resolveBenchmarkSubject]);

  const onResetToPolicy = useCallback(() => {
    setIsInEditState(false);
    setLocalConstraintState(policyBenchmarkConstraint);
    resolveBenchmarkSubject(policyBenchmarkConstraint);
    onResetToPolicyBenchmarkConstraint();
  }, [setIsInEditState, policyBenchmarkConstraint, resolveBenchmarkSubject, onResetToPolicyBenchmarkConstraint]);

  const onApplyToPolicy = useCallback(() => {
    const updated = isNil(localConstraint) ? undefined : { ...localConstraint, isInPortfolioPolicy: true };
    onApplyBenchmarkConstraintToPolicy(updated);
    setLocalConstraintState(updated);
  }, [localConstraint, onApplyBenchmarkConstraintToPolicy]);

  const hasNoChanges =
    localConstraint?.benchmarkFundId === benchmarkConstraint?.benchmarkFundId &&
    localConstraint?.benchmarkStrategyId === benchmarkConstraint?.benchmarkStrategyId &&
    localConstraint?.maxTrackingError === benchmarkConstraint?.maxTrackingError;

  useEffect(() => {
    if (isInEditState && hasNoChanges) {
      setIsInEditState(false);
    }
  }, [isInEditState, hasNoChanges, setIsInEditState]);

  if (loadingPolicy || isNil(portfolio)) {
    return <Loading title="Loading portfolio policy" />;
  }

  if (policyError) {
    return null;
  }

  const matchesPolicy = isNil(localConstraint) ? isNil(policyBenchmarkConstraint) : localConstraint.isInPortfolioPolicy;
  const hasBenchmarkSubject = !isNil(localBenchmarkSubject);
  const hasMaxTrackingError = !isNil(localConstraint?.maxTrackingError);
  const hasInvalidCombination =
    hasBenchmarkSubject !== hasMaxTrackingError ||
    (hasMaxTrackingError && (localConstraint?.maxTrackingError ?? 0) < 0);
  const hasConstraintsError = hasInvalidCombination || hasNoChanges;

  return (
    <Container>
      <Content>
        <BenchmarkConstraintManagement
          constraint={localConstraint}
          constraintBenchmarkSubject={localBenchmarkSubject}
          onChangeBenchmarkSubject={onChangeBenchmarkSubject}
          onChangeMaxTrackingError={onChangeMaxTrackingError}
          smallScreen={!isSideBarMaximized}
        />
      </Content>
      <ConstraintsPolicyStatusFooter
        portfolio={portfolio}
        matchesPolicy={matchesPolicy}
        isSideBarMaximized={isSideBarMaximized}
        onResetToPolicyConstraints={onResetToPolicy}
        onApplyToPolicy={onApplyToPolicy}
        onConstraintsSave={onConstraintsSave}
        onConstraintsRevert={onConstraintsRevert}
        isInEditState={isInEditState}
        hasConstraintsRevamp
        hasConstraintsError={hasConstraintsError}
        disablePolicySync
        hasNoChanges={hasNoChanges}
      />
    </Container>
  );
};

export default BenchmarkConstraints;

const Container = styled.div`
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 20px 60px 20px;
`;

const Content = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;
